import { chakra } from '@chakra-ui/react';
import { type ReactNode } from 'react';
import { useIsCompact } from './useMediaQuery';

const Root = chakra('div', {
  base: {
    display: 'flex',
    height: '100dvh',
    minHeight: '100dvh',
    width: '100%',
    overflow: 'hidden',
    bg: 'surface.subtle',
    color: 'text.primary',
  },
});

const Main = chakra('div', {
  base: { display: 'flex', flexDirection: 'column', flex: '1', minWidth: 0, minHeight: 0 },
});

const TopBar = chakra('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '3',
    minH: '14',
    px: { base: '4', md: '6' },
    flexShrink: 0,
    bg: 'surface.default',
    borderBottomWidth: '1px',
    borderColor: 'border.subtle',
    zIndex: 'docked',
  },
});

const TopStart = chakra('div', {
  base: { display: 'flex', alignItems: 'center', gap: '3', minWidth: 0, flex: '1' },
});

const TopEnd = chakra('div', {
  base: { display: 'flex', alignItems: 'center', gap: '1.5', flexShrink: 0 },
});

const Scroll = chakra('main', {
  base: { flex: '1', minHeight: 0, overflowY: 'auto', overflowX: 'hidden' },
});

const maxWidthToken = {
  narrow: 'content.narrow',
  default: 'content.default',
  wide: 'content.wide',
  full: 'none',
} as const;

const Inner = chakra('div', {
  base: { mx: 'auto', width: '100%', px: { base: '4', md: '6' }, py: { base: '5', md: '8' } },
});

export interface AppShellProps {
  /** Desktop navigation rail (>= md). */
  rail: ReactNode;
  /** Mobile/tablet bottom navigation (< md). */
  bottomNav: ReactNode;
  /** Brand mark shown top-left on mobile (the rail carries it on desktop). */
  brand?: ReactNode;
  /** Contextual left content on desktop (section / breadcrumb). */
  topBarStart?: ReactNode;
  /** Global-action cluster on the right (search, theme, notifications, sign-out…). */
  topBarEnd?: ReactNode;
  /** Version footer (rendered under the scrolling content). */
  footer?: ReactNode;
  /** Persistent overlays — the quick-chat launcher/panel and the command menu. */
  overlays?: ReactNode;
  children: ReactNode;
  maxWidth?: keyof typeof maxWidthToken;
}

/**
 * The "Operator's Console" application frame: a compact navigation rail (desktop) or a
 * bottom tab bar (mobile), a slim contextual top bar with a right-hand action cluster,
 * a scrolling main content region, a version footer, and slots for persistent overlays
 * (quick-chat, command menu). Editorial, dark-mode aware, and NOT a heavy drawer.
 *
 * Presentational only: it takes the navigation, actions, and overlays as slots so the
 * feature layer owns all data wiring (auth, notifications, chat) while this frame stays
 * in the design-system layer.
 */
export function AppShell({
  rail,
  bottomNav,
  brand,
  topBarStart,
  topBarEnd,
  footer,
  overlays,
  children,
  maxWidth = 'default',
}: AppShellProps) {
  const compact = useIsCompact();

  return (
    <Root data-shell-variant={compact ? 'compact' : 'expanded'}>
      {!compact && rail}
      <Main>
        <TopBar as="header">
          <TopStart>{compact ? brand : topBarStart}</TopStart>
          {topBarEnd && <TopEnd>{topBarEnd}</TopEnd>}
        </TopBar>
        <Scroll>
          <Inner maxWidth={maxWidthToken[maxWidth]}>{children}</Inner>
          {footer}
        </Scroll>
        {compact && bottomNav}
      </Main>
      {overlays}
    </Root>
  );
}
